import type { ResolvedSearchSection } from '@hibro/shared';
import {
  parseModuleSearchHits,
  resolveSearchOrder,
  scopeCovers,
} from '@hibro/shared';

import type { TitleResolver } from '../../commands/command-query';
import {
  ALL_TAB_SECTIONS,
  type SearchProvider,
  type SearchResult,
  type SearchSection,
  type SearchTab,
} from '../search-contract';

export interface ModuleProviderDeps {
  /** Runs the section's command; the workbench's dispatcher, so it auto-activates. */
  executeCommand: (id: string, ...args: unknown[]) => Promise<unknown>;
}

/**
 * Module-contributed search sections as providers (需求 FR-14 §5.3.2).
 *
 * One declared section becomes one provider, which is what makes the platform's
 * per-provider handling fall out for free: sections order independently, and a
 * section whose engine call fails costs only its own segment instead of the
 * module's whole tab.
 *
 * The call rides `executeCommand`, the sole existing renderer→ext-host route
 * for reaching a module's handler. That is also what supplies lazy activation:
 * the command is declared in the module's manifest (`validateManifest` insists
 * on it), so invoking it fires `onCommand:<id>` and pulls the module up — IM
 * answers a search without ever having been opened.
 *
 * No cancellation: a command cannot carry an `AbortSignal` across the boundary.
 * `runSearch` discards results from a superseded query, so a stale answer never
 * reaches the list; the module's work for it is simply wasted.
 */
export function createModuleSearchProviders(
  sections: readonly ResolvedSearchSection[],
  deps: ModuleProviderDeps,
): SearchProvider[] {
  return [...sections]
    .sort(
      (a, b) =>
        resolveSearchOrder(a.order, 'app') - resolveSearchOrder(b.order, 'app'),
    )
    .map((section, index) => ({
      id: section.key,
      section: section.key,
      moduleId: section.moduleId,
      ...(section.scope === undefined ? {} : { scope: section.scope }),
      rank: index,
      search: async (query, signal) => {
        const raw = await deps.executeCommand(section.command, {
          // `text`, not `terms`: the module matches this itself, so it needs the
          // user's input with its case intact (见 `SearchQuery.text`).
          query: query.text,
          ...(query.limit === undefined ? {} : { limit: query.limit }),
          // Straight through, unread (见 `ModuleSearchRequest.filters`). Only
          // the owning module's sections ever get here, since filters live on
          // an app tab.
          ...(query.filters === undefined ? {} : { filters: query.filters }),
        });
        if (signal.aborted) {
          return [];
        }
        // Throws on a malformed answer, which `runSearch` turns into a visible
        // error row for this section. Treating it as "no hits" would make a
        // broken module indistinguishable from an empty index.
        return parseModuleSearchHits(raw).map<SearchResult>((hit) => ({
          key: `${section.key}:${hit.id}`,
          providerId: section.key,
          section: section.key,
          title: hit.title,
          ...(hit.subtitle === undefined ? {} : { subtitle: hit.subtitle }),
          ...(hit.icon === undefined ? {} : { icon: hit.icon }),
          ...(hit.badge === undefined ? {} : { badge: hit.badge }),
          ...(typeof hit.personId === 'string' && hit.personId
            ? { personId: hit.personId }
            : {}),
          score: hit.score ?? 0,
          action: hit.action,
        }));
      },
    }));
}

/** Display title for a module section's heading, `%key%` resolved. */
export function resolveSectionTitle(
  section: ResolvedSearchSection,
  resolveTitle: TitleResolver,
): string {
  return resolveTitle(section.moduleId, section.title);
}

/**
 * The app tabs a module-section set implies, in display order.
 *
 * Grouped by module because the requirement's tab axis is **per application**
 * (需求 §5.2), with the app's registered sections living *inside* its tab. A
 * module contributing no section gets no tab, so the strip disappears entirely
 * until something registers — the same occupancy rule the auxiliaryBar follows
 * (见 03).
 *
 * Only `app`-scoped sections count. A module that declared nothing but an
 * All-tab section (contacts' people) has no tab of its own to show; giving it
 * one would put an empty tab on the strip whose only content is that module's
 * commands.
 */
export function appTabModuleIds(
  sections: readonly ResolvedSearchSection[],
): string[] {
  const lowestOrder = new Map<string, number>();
  for (const section of sections) {
    if (!scopeCovers(section.scope, 'app')) {
      continue;
    }
    const order = resolveSearchOrder(section.order, 'app');
    const seen = lowestOrder.get(section.moduleId);
    if (seen === undefined || order < seen) {
      lowestOrder.set(section.moduleId, order);
    }
  }
  return [...lowestOrder.keys()].sort(
    (a, b) => (lowestOrder.get(a) ?? 0) - (lowestOrder.get(b) ?? 0),
  );
}

/**
 * Section ids in display order for `tab`: platform constants first, then every
 * module section that answers under that tab, by the order resolved for it.
 *
 * Hand this to {@link mergeResults} so a late-arriving module section still
 * sits where the manifest said — arrival order is not a seat.
 */
export function sectionOrderForTab(
  sections: readonly ResolvedSearchSection[],
  tab: SearchTab,
): readonly SearchSection[] {
  const scope = tab.kind === 'all' ? 'all' : 'app';
  const moduleKeys = [...sections]
    .filter((section) => scopeCovers(section.scope, scope))
    .sort(
      (a, b) =>
        resolveSearchOrder(a.order, scope) - resolveSearchOrder(b.order, scope),
    )
    .map((section) => section.key);
  return [...ALL_TAB_SECTIONS, ...moduleKeys];
}
